import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, MenuItem, Paper, Stack, Switch, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Typography } from '@mui/material';
import { apiService } from '../services/api';
import { YamalConfiguration, YamalDevice, YamalReport, YamalReportRow, YamalSettings } from '../types/yamalMailing';

const statusLabels: Record<YamalReport['status'], string> = {
  generating: 'Формируется', ready: 'Готова', failed: 'Ошибка формирования',
  sending: 'Отправляется', sent: 'Отправлена', send_failed: 'Отправка не подтверждена',
};
const modeLabels = { manual: 'Ручная', test: 'Тестовая', auto: 'По расписанию' };
const dateText = (value?: string | null) => value
  ? new Date(value).toLocaleString('ru-RU', { timeZone: 'Europe/Moscow' }) : '—';
const addresses = (value: string) => Array.from(new Set(value.split(/[\s,;]+/).map((address) => address.trim().toLowerCase()).filter(Boolean)));
const durationText = (seconds: number | null) => seconds === null ? 'Нет данных' : seconds < 60 ? `${seconds} сек`
  : seconds < 3600 ? `${Math.floor(seconds / 60)} мин` : `${Math.floor(seconds / 3600)} ч ${Math.floor(seconds / 60) % 60} мин`;
const connectionDurationText = (row: YamalReportRow) => row.durationStatus === 'open' ? 'Отключение не зафиксировано'
  : row.durationStatus === 'missing' ? 'Нет истории' : row.durationStatus === 'unknown_start' ? 'Начало не зафиксировано'
    : durationText(row.connectionDurationSeconds);

export default function YamalMailingPage() {
  const [settings, setSettings] = useState<YamalSettings | null>(null);
  const [recipients, setRecipients] = useState('');
  const [testRecipients, setTestRecipients] = useState('');
  const [dirty, setDirty] = useState(false);
  const [configuration, setConfiguration] = useState<YamalConfiguration | null>(null);
  const [devices, setDevices] = useState<YamalDevice[]>([]);
  const [devicesError, setDevicesError] = useState<string | null>(null);
  const [history, setHistory] = useState<YamalReport[]>([]);
  const [report, setReport] = useState<YamalReport | null>(null);
  const [busy, setBusy] = useState(true);
  const [message, setMessage] = useState<{ severity: 'success' | 'error'; text: string } | null>(null);
  const [confirmSend, setConfirmSend] = useState(false);

  const run = useCallback(async (action: () => Promise<void>) => {
    setBusy(true);
    setMessage(null);
    try { await action(); } catch (error: any) {
      const text = error?.response?.data?.message || error?.message || 'Не удалось выполнить действие';
      setMessage({ severity: 'error', text: Array.isArray(text) ? text.join('; ') : String(text) });
    } finally { setBusy(false); }
  }, []);

  useEffect(() => {
    let active = true;
    void run(async () => {
      const [savedSettings, deviceResult, savedHistory, sourceConfiguration] = await Promise.all([
        apiService.getYamalSettings(),
        apiService.getYamalDevices().then((data) => ({ data, error: null })).catch(() => ({ data: [], error: 'Не удалось получить устройства из Panel. Проверьте ключ, адрес и TLS-сертификат.' })),
        apiService.getYamalReports(), apiService.getYamalConfiguration(),
      ]);
      if (!active) return;
      setSettings(savedSettings); setRecipients(savedSettings.recipients.join('\n'));
      setDevices(deviceResult.data); setDevicesError(deviceResult.error); setHistory(savedHistory); setConfiguration(sourceConfiguration);
    });
    return () => { active = false; };
  }, [run]);

  const refreshHistory = async () => setHistory(await apiService.getYamalReports());
  const sourcesReady = Boolean(configuration?.panelConfigured && configuration?.feedbackConfigured);
  const updateSetting = (value: Partial<YamalSettings>) => {
    setSettings((previous) => previous ? { ...previous, ...value } : null); setDirty(true);
  };
  const performReportAction = (action: () => Promise<YamalReport>, text: string) => run(async () => {
    setReport(null);
    try {
      setReport(await action()); setMessage({ severity: 'success', text });
    } finally { await refreshHistory(); }
  });
  const download = () => run(async () => {
    if (!report) return;
    const blob = await apiService.downloadYamalReport(report.id);
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url; anchor.download = `yamal_feedback_${report.id}.xlsx`; anchor.click();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  });

  return <Stack spacing={3}>
    <Box><Typography variant="h4">Ямал · сводка обратной связи</Typography>
      <Typography color="text.secondary">Устройства и последнее появление в сети — из Panel, длительность — из history, дата упаковки архива с анкетами — из ЛК.</Typography>
      <Typography variant="body2" color="text.secondary">Время появления в сети сохранено как в Panel; часовой пояс API не указан. Даты упаковки архивов и писем — МСК.</Typography></Box>
    {message && <Alert severity={message.severity}>{message.text}</Alert>}
    {busy && <Box role="status" aria-label="Загрузка"><CircularProgress size={24} /></Box>}
    {configuration && <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
      <Chip label={`Panel: ${configuration.panelConfigured ? 'ключ задан' : 'не настроен'}`} color={configuration.panelConfigured ? 'success' : 'warning'} />
      <Chip label={`API анкет: ${configuration.feedbackConfigured ? 'ключ задан' : 'нет ключа'}`} color={configuration.feedbackConfigured ? 'success' : 'warning'} />
      <Chip label={`SMTP: ${configuration.smtpConfigured ? 'настроен' : 'не настроен'}`} color={configuration.smtpConfigured ? 'success' : 'warning'} />
    </Stack>}
    {configuration && !configuration.panelConfigured && <Alert severity="warning">{configuration.panelMessage}. Формирование и отправка недоступны до подключения источника.</Alert>}
    {settings && <Paper sx={{ p: 3 }}><Stack spacing={2}>
      <Typography variant="h6">Рассылка</Typography>
      <TextField label="Получатели рассылки" multiline minRows={2} value={recipients} disabled={busy}
        helperText="Email через запятую, точку с запятой или с новой строки"
        onChange={(event) => { setRecipients(event.target.value); setDirty(true); }} />
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField select label="Расписание" value={settings.scheduleType} disabled={busy} sx={{ minWidth: 200 }}
          onChange={(event) => updateSetting({ scheduleType: event.target.value as YamalSettings['scheduleType'] })}>
          <MenuItem value="friday">Каждую пятницу</MenuItem><MenuItem value="daily">Ежедневно</MenuItem>
        </TextField>
        <TextField label="Время (МСК)" type="time" value={settings.sendTime} disabled={busy}
          onChange={(event) => updateSetting({ sendTime: event.target.value })} slotProps={{ inputLabel: { shrink: true } }} />
        <FormControlLabel control={<Switch checked={settings.isEnabled} disabled={busy}
          onChange={(_, checked) => updateSetting({ isEnabled: checked })} />} label="Автоматическая отправка" />
      </Stack>
      <Typography variant="body2" color="text.secondary">Последняя автоотправка: {dateText(settings.lastAutoSentAt)}. Последняя попытка: {settings.lastAutoAttemptDate || '—'}.</Typography>
      <Box><Button variant="contained" disabled={busy || !dirty} onClick={() => void run(async () => {
        const saved = await apiService.saveYamalSettings({ ...settings, recipients: addresses(recipients) });
        setSettings(saved); setRecipients(saved.recipients.join('\n')); setDirty(false);
        setMessage({ severity: 'success', text: 'Настройки сохранены' });
      })}>Сохранить настройки</Button></Box>
      {dirty && <Alert severity="info">Есть несохранённые настройки. Сохраните их перед формированием сводки.</Alert>}
      <TextField label="Адреса для тестового письма" value={testRecipients} disabled={busy}
        onChange={(event) => setTestRecipients(event.target.value)} helperText="Тест сформирует свежую сводку и отправит только на эти адреса (до 10). Настройки рассылки не изменятся." />
      <Box><Button variant="outlined" disabled={busy || !sourcesReady || !configuration?.smtpConfigured || !addresses(testRecipients).length}
        onClick={() => void performReportAction(() => apiService.sendYamalTest(addresses(testRecipients)), 'Тестовое письмо отправлено')}>Сформировать и отправить тест</Button></Box>
    </Stack></Paper>}

    <Paper sx={{ p: 3 }}><Stack spacing={2}>
      <Typography variant="h6">Устройства и борта из Panel · {devices.length}</Typography>
      <Typography variant="body2" color="text.secondary">Эталон — API Panel, client_id=3 (Ямал). В сводку входят все возвращённые устройства, включая отключённые. Перед формированием список запрашивается заново, без ограничения на 14 строк.</Typography>
      {devicesError && <Alert severity="error">{devicesError}</Alert>}
      <TableContainer><Table size="small"><TableHead><TableRow>
        <TableCell>ID Panel</TableCell><TableCell>Серийный №</TableCell><TableCell>Борт</TableCell><TableCell>Включён в Panel</TableCell><TableCell>Последнее появление в сети (как в Panel)</TableCell>
      </TableRow></TableHead><TableBody>{devices.map((device) => <TableRow key={device.id}>
        <TableCell>{device.id}</TableCell><TableCell>{device.serial}</TableCell><TableCell>{device.aircraft}</TableCell><TableCell>{device.isEnabled ? 'Да' : 'Нет'}</TableCell>
        <TableCell>{device.lastOnline || 'Нет появлений в сети'}</TableCell>
      </TableRow>)}</TableBody></Table></TableContainer>
      <Box><Button disabled={busy} onClick={() => void run(async () => {
        setDevices([]); setDevicesError(null);
        try { setDevices(await apiService.getYamalDevices()); }
        catch (error) { setDevicesError('Не удалось обновить список из Panel. Старые устройства не подставляются.'); throw error; }
      })}>Обновить из Panel</Button></Box>
    </Stack></Paper>

    <Paper sx={{ p: 3 }}><Stack spacing={2}>
      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
        <Typography variant="h6">Сводка</Typography>
        <Button variant="contained" disabled={busy || dirty || !sourcesReady}
          onClick={() => void performReportAction(() => apiService.generateYamalReport(), 'Свежая сводка сформирована')}>Сформировать свежую сводку</Button>
      </Stack>
      {!report && <Typography color="text.secondary">Сформируйте сводку или откройте запись из истории. Старые данные не подставляются при ошибке API.</Typography>}
      {report && <>
        <Typography>Сводка №{report.id} · {statusLabels[report.status]} · {dateText(report.generatedAt)} МСК</Typography>
        <Typography variant="body2">Получатели этой сводки: {report.recipients.join(', ') || 'не заданы'}</Typography>
        {report.error && <Alert severity="error">{report.error}</Alert>}
        {!!report.rows?.length && <>
          <TableContainer><Table size="small"><TableHead><TableRow>
            {['Серийный №', 'Борт', (report.formatVersion || 1) >= 2 ? 'Последнее появление в сети (как в Panel)' : 'Начало последнего подключения', 'Длительность', (report.formatVersion || 1) >= 3 ? 'Дата и время упаковки последнего архива с анкетами пассажиров (МСК)' : 'Последняя выгрузка анкет (МСК)', 'Новые анкеты на момент сводки'].map((header) => <TableCell key={header}>{header}</TableCell>)}
          </TableRow></TableHead><TableBody>{report.rows.map((row) => <TableRow key={row.serial}>
            <TableCell>{row.serial}</TableCell><TableCell>{row.aircraft}</TableCell>
            <TableCell>{(report.formatVersion || 1) >= 2 ? (row.lastOnline || 'Нет появлений в сети') : (row.connectionStartedAt ? dateText(row.connectionStartedAt) : 'Нет данных')}</TableCell>
            <TableCell>{connectionDurationText(row)}</TableCell>
            <TableCell>{(report.formatVersion || 1) >= 3 ? (row.archivePackedAt ? dateText(row.archivePackedAt) : 'Нет архивов') : (row.lastExportAt ? dateText(row.lastExportAt) : 'Нет выгрузок')}</TableCell>
            <TableCell>{row.hasNewForms ? `Да (${row.newFormsCount})` : 'Нет (0)'}</TableCell>
          </TableRow>)}</TableBody></Table></TableContainer>
          <Typography variant="caption">XLSX содержит 5 колонок по эталону. «Новые анкеты» — дополнительная информация только на странице. {(report.formatVersion || 1) >= 3 ? 'Пятая колонка — дата упаковки архива, не дата его выгрузки.' : 'Исторический формат: пятая колонка — дата выгрузки, не упаковки архива.'}</Typography>
          <Stack direction="row" spacing={2}>
            <Button disabled={busy} onClick={() => void download()}>Скачать XLSX</Button>
            <Button variant="contained" disabled={busy || report.status !== 'ready' || !report.recipients.length || !configuration?.smtpConfigured}
              onClick={() => setConfirmSend(true)}>Отправить эту сводку</Button>
          </Stack>
        </>}
      </>}
    </Stack></Paper>

    <Paper sx={{ p: 3 }}><Stack spacing={2}>
      <Stack direction="row" spacing={2}><Typography variant="h6">История · последние 50 сводок</Typography>
        <Button disabled={busy} onClick={() => void run(refreshHistory)}>Обновить</Button></Stack>
      <TableContainer><Table size="small"><TableHead><TableRow>
        {['№', 'Создана (МСК)', 'Тип', 'Статус', 'Отправлена (МСК)', ''].map((header) => <TableCell key={header}>{header}</TableCell>)}
      </TableRow></TableHead><TableBody>{history.map((item) => <TableRow key={item.id}>
        <TableCell>{item.id}</TableCell><TableCell>{dateText(item.createdAt)}</TableCell><TableCell>{modeLabels[item.mode]}</TableCell>
        <TableCell>{statusLabels[item.status]}</TableCell><TableCell>{dateText(item.sentAt)}</TableCell>
        <TableCell><Button disabled={busy} onClick={() => void run(async () => setReport(await apiService.getYamalReport(item.id)))}>Открыть</Button></TableCell>
      </TableRow>)}</TableBody></Table></TableContainer>
    </Stack></Paper>

    <Dialog open={confirmSend} onClose={() => setConfirmSend(false)}>
      <DialogTitle>Отправить сводку №{report?.id}?</DialogTitle>
      <DialogContent><Typography>Будет отправлен сохранённый снимок от {dateText(report?.generatedAt)} МСК, без повторного запроса API.</Typography>
        <Typography sx={{ mt: 2 }}>{report?.recipients.join(', ')}</Typography></DialogContent>
      <DialogActions><Button onClick={() => setConfirmSend(false)}>Отмена</Button>
        <Button onClick={() => { setConfirmSend(false); if (report) void performReportAction(() => apiService.sendYamalReport(report.id), 'Сводка отправлена'); }}>Отправить</Button></DialogActions>
    </Dialog>
  </Stack>;
}
